import {
  getBreakdownProcessV1,
  getBreakdownProcessV2,
  getChildPartV1,
  getChildPartV2,
  createChildPartV2,
  getPartPriceHistory,
  createMaterialProcessV2,
  type materialProcessParams,
  createChildPartProcessV2,
  type childPartProcessParams,
  createPartProcessV2,
  type partProcessParams,
  createPartOtherProcessV2,
  type partOtherProcessParams,
  updateMaterialProcessV2,
  updateChildPartProcessV2,
  updatePartProcessV2,
  updatePartOtherProcessV2,
  deleteMaterialProcessV2,
  deleteChildPartProcessV2,
  deletePartProcessV2,
  deletePartOtherProcessV2,
  type childPartV2Param,
  updateChildPartV2,
  getPartProcessV2,
  getPartOtherProcessV2,
  updatePartProcessV2Detail,
  type partProcess,
  updatePartOtherProcessV2Detail,
  type partOtherProcess,
  deleteChildPartV2,
} from "@/api/breakdownService";
import { CACHE_TIME } from "@/lib/cacheConfig";
import { queryKeys } from "@/lib/queryKeys";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useQueryInvalidation } from "@/lib/queryInvalidation";

export const useGetChildPartV1 = (
  partId: number,
  version: "v1" | "v2" | undefined,
) => {
  return useQuery({
    queryKey: queryKeys.breakdown.childPartV1(partId),
    queryFn: () => getChildPartV1(partId),
    enabled: !!partId && version === "v1",
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useGetBreakdownProcessV1 = (supplierCode: string) => {
  return useQuery({
    queryKey: queryKeys.breakdown.breakdownProcessV1(supplierCode),
    queryFn: () => getBreakdownProcessV1(supplierCode),
    enabled: !!supplierCode,
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useGetBreakdownProcessV2 = (partId: number) => {
  return useQuery({
    queryKey: queryKeys.breakdown.breakdownProcessV2(partId),
    queryFn: () => getBreakdownProcessV2(partId),
    enabled: !!partId,
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useGetChildPartV2 = (
  partId: number,
  version: "v1" | "v2" | undefined,
) => {
  return useQuery({
    queryKey: queryKeys.breakdown.childPartV2(partId),
    queryFn: () => getChildPartV2(partId),
    enabled: !!partId && version === "v2",
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useCreateChildPartV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.createChildPartV2(),
    mutationFn: ({
      partId,
      supplierCode,
      params,
      flagged_reason,
    }: {
      partId: number;
      supplierCode: string;
      params: childPartV2Param;
      flagged_reason: Set<string>;
    }) => createChildPartV2(partId, supplierCode, params, flagged_reason),
    onSuccess: () => {
      queryInvalidation.invalidateChildPartV2();
    },
  });
};

export const useUpdateChildPartV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.updateChildPartV2(),
    mutationFn: ({
      partId,
      supplierCode,
      params,
      flagged_reason,
    }: {
      partId: number;
      supplierCode: string;
      params: Array<childPartV2Param>;
      flagged_reason: Set<string>;
    }) => updateChildPartV2(partId, supplierCode, params, flagged_reason),
    onSuccess: () => {
      queryInvalidation.invalidateChildPartV2();
    },
  });
};

export const useDeleteChildPartV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.deleteChildPartV2(),
    mutationFn: ({
      partId,
      childPartIdList,
    }: {
    partId: number;
    childPartIdList: Array<number>;
    }) => deleteChildPartV2(partId, childPartIdList),
    onSuccess: () => {
      queryInvalidation.invalidateChildPartV2();
    },
  });
};

export const useCreateMaterialProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.createMaterialProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: materialProcessParams;
    }) => createMaterialProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useUpdateMaterialProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.updateMaterialProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: materialProcessParams;
    }) => updateMaterialProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useDeleteMaterialProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.deleteMaterialProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: materialProcessParams;
    }) => deleteMaterialProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useCreateChildPartProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.createChildPartProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: childPartProcessParams;
    }) => createChildPartProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useUpdateChildPartProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.updateChildPartProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: childPartProcessParams;
    }) => updateChildPartProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useDeleteChildPartProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.deleteChildPartProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: childPartProcessParams;
    }) => deleteChildPartProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useCreatePartProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.createPartProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: partProcessParams;
    }) => createPartProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useUpdatePartProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.updatePartProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: partProcessParams;
    }) => updatePartProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useDeletePartProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.deletePartProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: partProcessParams;
    }) => deletePartProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useCreatePartOtherProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.createPartOtherProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: partOtherProcessParams;
    }) => createPartOtherProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useUpdatePartOtherProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.updatePartOtherProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: partOtherProcessParams;
    }) => updatePartOtherProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useDeletePartOtherProcessV2 = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.deletePartOtherProcessV2(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: partOtherProcessParams;
    }) => deletePartOtherProcessV2(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useGetPartProcessV2 = (partId: number, enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.breakdown.partProcessV2(partId),
    queryFn: () => getPartProcessV2(partId),
    enabled: !!partId && enabled,
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useGetPartOtherProcessV2 = (partId: number, enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.breakdown.partOtherProcessV2(partId),
    queryFn: () => getPartOtherProcessV2(partId),
    enabled: !!partId && enabled,
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useUpdatePartProcessV2Detail = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.updatePartProcessV2Detail(),
    mutationFn: ({ partId, params }: { partId: number; params: partProcess }) =>
      updatePartProcessV2Detail(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useUpdatePartOtherProcessV2Detail = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.breakdown.updatePartOtherProcessV2Detail(),
    mutationFn: ({
      partId,
      params,
    }: {
      partId: number;
      params: partOtherProcess;
    }) => updatePartOtherProcessV2Detail(partId, params),
    onSuccess: () => {
      queryInvalidation.invalidateBreakdownProcess();
    },
  });
};

export const useGetPartPriceHistory = (
  partId: number,
  version: "v1" | "v2",
) => {
  return useQuery({
    queryKey: queryKeys.breakdown.partPriceHistory(partId, version),
    queryFn: () => getPartPriceHistory(version, partId),
    enabled: !!partId,
    staleTime: CACHE_TIME.SHORT,
  });
};
